// Mobile shell (<1100px): header (wordmark · ◆ N · ⚙), scrolling content,
// the fixed gap bar (LOG only), the bottom tab bar, and the bottom sheet that
// hosts the same inspectors desktop shows in its right column.

import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, type ReactNode } from 'react';
import { mondayOf } from '../../lib/cardio-queries';
import { C, FONT_DISPLAY, FONT_MONO } from '../../lib/design';
import { dowLabel } from '../../lib/ledger';
import { useLedger } from '../../lib/ledger-state';
import { useLedgerWeek } from '../../lib/ledger-week';
import { DataHealthPill } from '../DataHealthPill';
import { PendingWritesPill } from '../PendingWritesPill';
import { useCoachPendingCount } from './Header';
import { Inspector } from './Inspector';
import { Btn } from './ui';

const TABS: Array<{ to: '/log' | '/plan' | '/trends'; label: string }> = [
  { to: '/log', label: 'LOG' },
  { to: '/plan', label: 'PLAN' },
  { to: '/trends', label: 'TRENDS' },
];

/** Bottom sheet: scrim + panel sliding up from the bottom (250ms). */
export function Sheet({ open, onClose, children }: { open: boolean; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 80, pointerEvents: open ? 'auto' : 'none' }}>
      <div
        onClick={onClose}
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,.6)',
          opacity: open ? 1 : 0,
          transition: 'opacity .2s',
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          maxHeight: '88%',
          background: C.panel,
          borderTop: `1px solid ${C.line}`,
          transform: open ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform .25s ease',
          display: 'flex',
          flexDirection: 'column',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0 0' }}>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={{ width: 36, height: 4, background: C.faint, padding: 0, border: 0 }}
          />
        </div>
        <div style={{ overflowY: 'auto', paddingBottom: 40, WebkitOverflowScrolling: 'touch' }}>
          {open && children}
        </div>
      </div>
    </div>
  );
}

/** The week's gaps for the selected date's week — no dish items needed. */
function useWeekGaps() {
  const { date } = useLedger();
  return useLedgerWeek(mondayOf(date), []).gaps;
}

function GapBar() {
  const ledger = useLedger();
  const gaps = useWeekGaps();
  const first = gaps[0];
  if (!first) return null;
  return (
    <button
      type="button"
      onClick={() => ledger.select(first.date, first.row)}
      style={{
        position: 'fixed',
        left: 16,
        right: 16,
        bottom: 'calc(62px + env(safe-area-inset-bottom) + 12px)',
        background: C.panel2,
        border: `1px dashed ${C.warn}`,
        color: C.warn,
        fontFamily: FONT_MONO,
        fontSize: 10,
        letterSpacing: '0.15em',
        padding: '12px 14px',
        minHeight: 44,
        display: 'flex',
        justifyContent: 'space-between',
        gap: 12,
        cursor: 'pointer',
        zIndex: 5,
      }}
    >
      <span>
        {gaps.length} GAP{gaps.length > 1 ? 'S' : ''} THIS WEEK
      </span>
      <span>
        FILL {dowLabel(first.date)} {first.row.toUpperCase()} →
      </span>
    </button>
  );
}

/** Inside the sheet: jump straight to the next gap (the backfill loop). */
function NextGapButton() {
  const ledger = useLedger();
  const gaps = useWeekGaps();
  const next = gaps.find((g) => !(g.date === ledger.date && g.row === ledger.row));
  if (!next) return null;
  return (
    <div style={{ padding: '0 16px' }}>
      <Btn
        onClick={() => ledger.select(next.date, next.row)}
        style={{ width: '100%', padding: 12, color: C.warn, borderColor: C.warn, borderStyle: 'dashed' }}
      >
        NEXT GAP · {dowLabel(next.date)} {next.row.toUpperCase()} →
      </Btn>
    </div>
  );
}

export function MobileShell({ children }: { children: ReactNode }) {
  const path = useRouterState().location.pathname;
  const navigate = useNavigate();
  const ledger = useLedger();
  const pending = useCoachPendingCount();
  const onLog = path === '/log';
  const sheetOpen = ledger.coach || (onLog && ledger.row != null);

  return (
    <div
      style={{
        background: C.bg,
        color: C.text,
        fontFamily: FONT_MONO,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '14px 16px 10px',
        }}
      >
        <Link to="/log" style={{ fontFamily: FONT_DISPLAY, fontSize: 20, letterSpacing: '-0.02em', color: C.text }}>
          <span style={{ fontWeight: 300, fontStyle: 'italic' }}>the</span> protocol
        </Link>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <DataHealthPill compactWhenHealthy height={36} />
          <PendingWritesPill />
          <button
            type="button"
            onClick={ledger.openCoach}
            aria-label="Coach"
            style={{
              background: 'transparent',
              border: `1px solid ${pending ? C.signal : C.line}`,
              color: pending ? C.signal : C.dim,
              fontFamily: FONT_MONO,
              fontSize: 10,
              letterSpacing: '0.15em',
              padding: '8px 10px',
              minHeight: 36,
              cursor: 'pointer',
            }}
          >
            ◆{pending ? ` ${pending}` : ''}
          </button>
          <Link
            to="/settings"
            aria-label="Settings"
            style={{
              color: path === '/settings' ? C.text : C.dim,
              fontSize: 18,
              minWidth: 36,
              minHeight: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ⚙
          </Link>
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, paddingBottom: 'calc(62px + env(safe-area-inset-bottom))' }}>
        {children}
      </div>

      {onLog && !sheetOpen && <GapBar />}

      <nav
        aria-label="Primary"
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          borderTop: `1px solid ${C.line}`,
          background: C.bg,
          paddingBottom: 'env(safe-area-inset-bottom)',
          zIndex: 10,
        }}
      >
        {TABS.map((t) => {
          const active = path === t.to;
          return (
            <button
              key={t.to}
              type="button"
              aria-current={active ? 'page' : undefined}
              onClick={() => {
                ledger.closeSheet();
                navigate({ to: t.to });
              }}
              style={{
                flex: 1,
                background: 'transparent',
                border: 0,
                borderTop: `2px solid ${active ? C.signal : 'transparent'}`,
                color: active ? C.signal : C.dim,
                fontFamily: FONT_MONO,
                fontSize: 10,
                letterSpacing: '0.2em',
                padding: '14px 0 18px',
                cursor: 'pointer',
                minHeight: 52,
              }}
            >
              {t.label}
            </button>
          );
        })}
      </nav>

      <Sheet open={sheetOpen} onClose={ledger.closeSheet}>
        <Inspector />
        {onLog && !ledger.coach && ledger.row != null && <NextGapButton />}
      </Sheet>
    </div>
  );
}
